import {
    Box, Grid, Card, CardContent, Typography, Chip, Avatar,
    CircularProgress
} from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CustomToggle from "../../../Base/views/CustomToggle";
import { useState, useEffect } from "react";
import { GetMandateReportsAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { GetAtsCompanyAction } from "../../../../../store/ATS/Company/Action";
import { GetAtsGroupCompanyAction } from "../../../../../store/ATS/GroupCompany/Action";
import { useDispatch, useSelector } from "react-redux";
import { ConvertDateTime, getUserData } from "../../../Base/helper/baseFunctions";
import Chart from "react-apexcharts";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const CHART_PALETTE = ["#8B44FF", "#4E79A7", "#10B981", "#F28E2B", "#E15759", "#76B7B2", "#59A14F"];

const STATUS_STYLES = {
    "active":    { bg: "#D1FAE5", color: "#059669" },
    "open":      { bg: "#D1FAE5", color: "#059669" },
    "hold":      { bg: "#FEF3C7", color: "#D97706" },
    "closed":    { bg: "#F3E8FF", color: "#7C3AED" },
    "filled":    { bg: "#DBEAFE", color: "#2563EB" },
    "cancelled": { bg: "#FEE2E2", color: "#DC2626" },
};
const getStatusStyle = (status) => {
    const key = Object.keys(STATUS_STYLES).find(k => (status || "").toLowerCase().includes(k));
    return STATUS_STYLES[key] || { bg: "#F1F5F9", color: "#64748B" };
};

const formatCTC = (val) => {
    const n = Number(val) || 0;
    if (!n) return "—";
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n.toLocaleString("en-IN")}`;
};

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, sub, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                    {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600}>{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const MandateReportNew = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const user = getUserData();
    const isGroupOrg = user?.grouporg_flow === true;

    const [userData, setUserData] = useState({ load: true, values: [] });
    const [clientData, setClientData] = useState({ load: true, values: [] });
    const [groupOrgData, setGroupOrgData] = useState({ load: true, values: [] });
    const [filters, setFilters] = useState({
        primary_recruiter_filter: "",
        ats_company_id: "",
        ats_grouporg_id: "",
    });

    const { atsMandateReport, isLoading } = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(s => s.AtsReducer?.AtsReportReducer);
    const { atsGroupCompanyData } = useSelector(s => s.AtsReducer?.AtsGroupCompanyReducer);
    const { atsCompanyData } = useSelector(s => s.AtsReducer?.AtsCompanyReducer);

    // ── Load dropdowns ────────────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        if (isGroupOrg) dispatch(GetAtsGroupCompanyAction());
        else dispatch(GetAtsCompanyAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);


    const getClientData = () => {
        if (isGroupOrg) {
            atsGroupCompanyData && setGroupOrgData({ load: false, values: atsGroupCompanyData?.filter(el => el.status === "Active") });
        } else {
            atsCompanyData && setClientData({ load: false, values: atsCompanyData?.filter(el => el.status === "Active") });
        }
    };

    // ── Fetch mandate data ────────────────────────────────────────────────────
    useEffect(() => {
        let clientId = null;
        if (isGroupOrg) {
            clientId = filters.ats_grouporg_id ? `{${filters.ats_grouporg_id.ats_grouporg_id}}` : null;
        } else {
            clientId = filters.ats_company_id ? `{${filters.ats_company_id.ats_company_id}}` : null;
        }
        dispatch(GetMandateReportsAction({
            recruiter_id: filters.primary_recruiter_filter ? `{${filters.primary_recruiter_filter.ats_users_id}}` : null,
            client_id: clientId,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handleFilter = (event, value) => {
        const keys = ["primary_recruiter_filter", "ats_company_id", "ats_grouporg_id"];
        if (keys.includes(event)) setFilters(f => ({ ...f, [event]: value || "" }));
    };

    // ── Data ──────────────────────────────────────────────────────────────────
    const mandates = atsMandateReport || [];

    // KPIs
    const totalMandates = mandates?.length || 0;
    const activeMandates = mandates?.filter(m => (m.status || "").toLowerCase().includes("open") || (m.status || "").toLowerCase().includes("active")).length || 0;
    const avgDaysPending = totalMandates > 0
        ? Math.round(mandates?.reduce((s, m) => s + (parseInt(m.days_pending) || 0), 0) / totalMandates)
        : 0;
    const uniqueClients = new Set(mandates?.map(m => m.client_name)).size;

    // ── Status donut ──────────────────────────────────────────────────────────
    const statusCounts = {};
    mandates?.forEach(m => {
        const key = m.status || "Unknown";
        statusCounts[key] = (statusCounts[key] || 0) + 1;
    });
    const statusLabels = Object.keys(statusCounts);
    const donutChart = {
        options: {
            chart: { type: "donut", fontFamily: "Nunito Sans" },
            labels: statusLabels.length ? statusLabels : ["No Data"],
            colors: CHART_PALETTE.slice(0, statusLabels.length || 1),
            dataLabels: { enabled: false },
            stroke: { width: 0 },
            legend: { position: "bottom", fontSize: "11px", markers: { radius: 3 } },
            plotOptions: {
                pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Total", fontSize: "12px", color: "#94A3B8" } } } },
            },
        },
        series: statusLabels.length ? statusLabels.map(s => statusCounts[s]) : [1],
    };

    // ── Mandates by recruiter horizontal bar ──────────────────────────────────
    const recruiterCounts = {};
    mandates?.forEach(m => {
        const name = m.recruiter_name || "Unknown";
        recruiterCounts[name] = (recruiterCounts[name] || 0) + 1;
    });
    const topRecruiters = Object.entries(recruiterCounts)?.sort((a, b) => b[1] - a[1]).slice(0, 10);
    const recruiterBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND],
            plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%" } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 600 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
            xaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            yaxis: { labels: { style: { fontSize: "11px", fontWeight: 500 } } },
            legend: { show: false },
        },
        series: [{ name: "Mandates", data: topRecruiters.map(([name, count]) => ({ x: name.split(" ")[0], y: count })) }],
    };

    // ── CTC distribution bar ──────────────────────────────────────────────────
    const ctcBrackets = { "< 5L": 0, "5–10L": 0, "10–25L": 0, "25–50L": 0, "50L+": 0 };
    mandates?.forEach(m => {
        const ctc = Number(m.ctc) || 0;
        if (ctc < 500000) ctcBrackets["< 5L"]++;
        else if (ctc < 1000000) ctcBrackets["5–10L"]++;
        else if (ctc < 2500000) ctcBrackets["10–25L"]++;
        else if (ctc < 5000000) ctcBrackets["25–50L"]++;
        else ctcBrackets["50L+"]++;
    });
    const ctcBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: CHART_PALETTE.slice(0, 5),
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 5, distributed: true } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 600 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: { categories: Object.keys(ctcBrackets), labels: { style: { fontSize: "11px", fontWeight: 500 } } },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { show: false },
        },
        series: [{ name: "Count", data: Object.values(ctcBrackets) }],
    };

    // ── Days pending by client bar (top 8) ─────────────────────────────────
    const clientDays = {};
    mandates?.forEach(m => {
        const c = m.client_name || "Unknown";
        if (!clientDays[c]) clientDays[c] = { total: 0, count: 0 };
        clientDays[c].total += parseInt(m.days_pending) || 0;
        clientDays[c].count += 1;
    });
    const topClientsByDays = Object.entries(clientDays)
        .map(([name, v]) => ({ name, avg: Math.round(v.total / v.count) }))
        .sort((a, b) => b.avg - a.avg).slice(0, 8);

    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = [
        { title: "Recruiter", field: "recruiter_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        { title: isGroupOrg ? "Group Org" : "Client", field: "client_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
        {
            title: "Date of Mandate", field: "date_of_mandate",
            render: r => r?.date_of_mandate ? ConvertDateTime(r.date_of_mandate) : "—",
            cellStyle: { padding: "10px", whiteSpace: "nowrap" }, headerStyle: { padding: "10px" },
        },
        { title: "Job ID", field: "job_id", cellStyle: { padding: "10px", color: "#64748B", fontSize: "0.78rem" }, headerStyle: { padding: "10px" } },
        { title: "Job Title", field: "job_title", cellStyle: { padding: "10px", fontWeight: 600 }, headerStyle: { padding: "10px" } },
        { title: "Location", field: "job_location", cellStyle: { padding: "10px", color: "#64748B" }, headerStyle: { padding: "10px" } },
        {
            title: "CTC", field: "ctc", type: "numeric",
            render: r => <Typography fontWeight={700} fontSize="0.82rem" color={BRAND}>{formatCTC(r.ctc)}</Typography>,
            cellStyle: { padding: "10px", textAlign: "right" }, headerStyle: { padding: "10px", textAlign: "right" },
        },
        {
            title: "CTC Tag", field: "ctc_category",
            render: r => r.ctc_category ? (
                <Chip label={r.ctc_category} size="small" sx={{ bgcolor: "#F3EDFD", color: BRAND, fontWeight: 600, fontSize: "0.68rem" }} />
            ) : "—",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Days Pending", field: "days_pending", type: "numeric",
            render: r => {
                const d = parseInt(r.days_pending) || 0;
                const color = d > 60 ? "#DC2626" : d > 30 ? "#D97706" : "#059669";
                return <Chip label={`${d}d`} size="small" sx={{ bgcolor: `${color}15`, color, fontWeight: 700, fontSize: "0.7rem" }} />;
            },
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
        {
            title: "Status", field: "status",
            render: r => {
                const s = getStatusStyle(r.status);
                return <Chip label={r.status || "Unknown"} size="small" sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: "0.68rem" }} />;
            },
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters & View Toggle ─────────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    {isGroupOrg ? (
                        <Grid item xs={12} sm={4} md={3}>
                            <SelectableSearch
                                isOptionEqualToValue={(o, v) => +o.ats_grouporg_id === +v.ats_grouporg_id}
                                label="Group Organisation" value={filters.ats_grouporg_id}
                                loading={getClientData} fieldLabel="name" required={false}
                                variant="outlined" name="ats_grouporg_id"
                                onChangeEvent={handleFilter} data={groupOrgData} size="small"
                            />
                        </Grid>
                    ) : (
                        <Grid item xs={12} sm={4} md={3}>
                            <SelectableSearch
                                isOptionEqualToValue={(o, v) => +o.ats_company_id === +v.ats_company_id}
                                label="Client" value={filters.ats_company_id}
                                loading={getClientData} fieldLabel="name" required={false}
                                variant="outlined" name="ats_company_id"
                                onChangeEvent={handleFilter} data={clientData} size="small"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => +o.ats_users_id === +v.ats_users_id}
                            label="Recruiter" value={filters.primary_recruiter_filter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name" required={false}
                            variant="outlined" name="primary_recruiter_filter"
                            onChangeEvent={handleFilter} data={userData} size="small"
                        />
                    </Grid>
                    {isLoading && (
                        <Grid item xs="auto" sx={{ display: "flex", alignItems: "center" }}>
                            <CircularProgress size={22} disableShrink/>
                        </Grid>
                    )}  
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<AssignmentOutlinedIcon sx={{ fontSize: 20 }} />} value={totalMandates} label="Total Mandates" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<CheckCircleOutlineIcon sx={{ fontSize: 20 }} />} value={activeMandates} label="Active / Open" sub={`${totalMandates > 0 ? Math.round(activeMandates / totalMandates * 100) : 0}% of total`} color="#10B981" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<AccessTimeIcon sx={{ fontSize: 20 }} />} value={`${avgDaysPending}d`} label="Avg Days Pending" sub={avgDaysPending > 45 ? "⚠ High" : "✓ OK"} color={avgDaysPending > 45 ? "#E15759" : "#F28E2B"} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<BusinessOutlinedIcon sx={{ fontSize: 20 }} />} value={uniqueClients} label="Active Clients" color={BRAND} />
                            </Grid>
                        </Grid>

                        {/* ── Charts Row ────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={3}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Status Distribution</Typography>
                                        <Typography variant="caption" color="text.secondary">By current mandate status</Typography>
                                        {statusLabels.length > 0 ? (
                                            <Chart options={donutChart.options} series={donutChart.series} type="donut" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Mandates by Recruiter</Typography>
                                        <Typography variant="caption" color="text.secondary">Top 10 recruiters by mandate count</Typography>
                                        {topRecruiters.length > 0 ? (
                                            <Chart options={recruiterBar.options} series={recruiterBar.series} type="bar" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>CTC Distribution</Typography>
                                        <Typography variant="caption" color="text.secondary">Mandates by salary range bracket</Typography>
                                        {mandates.length > 0 ? (
                                            <Chart options={ctcBar.options} series={ctcBar.series} type="bar" height={230} />
                                        ) : (
                                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* ── Days Pending by Client ─────────────────────────────── */}
                        {topClientsByDays.length > 0 && (
                            <Card>
                                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Avg Days Pending by Client</Typography>
                                    <Grid container spacing={1}>
                                        {topClientsByDays.map(({ name, avg }) => {
                                            const color = avg > 60 ? "#DC2626" : avg > 30 ? "#D97706" : "#059669";
                                            const pct = Math.min((avg / 90) * 100, 100);
                                            return (
                                                <Grid item xs={12} sm={6} md={3} key={name}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: 1.5, border: "1px solid #F1F5F9" }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography fontSize="0.78rem" fontWeight={600} noWrap>{name}</Typography>
                                                            <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#F1F5F9", mt: 0.5, overflow: "hidden" }}>
                                                                <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: color, borderRadius: 2, transition: "width 0.6s ease" }} />
                                                            </Box>
                                                        </Box>
                                                        <Chip label={`${avg}d`} size="small" sx={{ bgcolor: `${color}15`, color, fontWeight: 700, fontSize: "0.68rem", flexShrink: 0 }} />
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}
                    </>
                ) : (
                    /* ── Detail Table ──────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <MaterialTable
                            columns={columns}
                            data={mandates}
                            isLoading={isLoading}
                            options={{
                                maxBodyHeight: "calc(100vh - 280px)",
                                minBodyHeight: "350px",
                                sorting: true,
                                search: true,
                                searchFieldAlignment: "right",
                                searchAutoFocus: true,
                                searchFieldVariant: "standard",
                                filtering: false,
                                exportButton: true,
                                exportAllData: true,
                                paging: true,
                                pageSizeOptions: [25, 50, 100],
                                pageSize: 25,
                                actionsColumnIndex: -1,
                                draggable: false,
                                columnsButton: true,
                                grouping: false,
                                headerStyle: {
                                    backgroundColor: "#fff",
                                    color: "#64748B",
                                    fontWeight: 700,
                                    fontSize: "0.78rem",
                                    whiteSpace: "nowrap",
                                    padding: "10px",
                                },
                            }}
                            title={`Mandates (${totalMandates})`}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
};

export default MandateReportNew;
